export function formatNumber(value: string | number | null): string {
    if (value === null || value === undefined) {
        return '-';
    }

    const numValue = typeof value === 'string' ? parseFloat(value) : value;

    if (isNaN(numValue)) {
        return value.toString();
    }

    // Format large numbers
    if (numValue >= 1000000) {
        return `${(numValue / 1000000).toFixed(1)}M`;
    }
    if (numValue >= 1000) {
        return `${(numValue / 1000).toFixed(1)}K`;
    }

    // Format with thousand separators for numbers >= 1000
    return numValue.toLocaleString();
}

/**
 * Format a number with thousand separators without abbreviation
 * @param value - The value to format
 * @returns Formatted number string with spaces as thousand separators (e.g., "12 847", "156 234")
 */
export function formatNumberFull(value: string | number | null): string {
    if (value === null || value === undefined) {
        return '-';
    }

    const numValue = typeof value === 'string' ? parseFloat(value) : value;

    if (isNaN(numValue)) {
        return value.toString();
    }

    return numValue.toLocaleString('cs-CZ');
}

export function formatBytes(bytes: number): string {
    if (bytes === 0) {
        return '0 B';
    }
    const k = 1024;
    const sizes = ['B', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

/**
 * Format a number as currency
 * @param amount - The amount to format
 * @param currency - Currency code (default: 'CZK')
 * @param locale - Locale for formatting (default: 'cs-CZ')
 * @returns Formatted currency string
 */
export function formatCurrency(amount: number, currency: string = 'CZK', locale: string = 'cs-CZ'): string {
    return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol',
    }).format(amount);
}

/**
 * Format duration in seconds to human-readable format
 * @param seconds - Duration in seconds
 * @returns Formatted duration string (e.g., "45.0 s", "1:30 min", "1:30:45 h")
 */
export function formatDuration(seconds: number): string {
    if (seconds < 60) {
        return `${seconds.toFixed(1)}`;
    }

    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);

    if (minutes < 60) {
        return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    }

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    return `${hours}:${remainingMinutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
}

/**
 * Get appropriate time unit for given duration in seconds
 * @param seconds - Duration in seconds
 * @returns Time unit string ('s', 'min', or 'h')
 */
export function getTimeUnit(seconds: number): string {
    if (seconds < 60) {
        return 's';
    }
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) {
        return 'min';
    }

    return 'h';
}

/**
 * Format a number as percentage
 * @param value - The value to format (0-100)
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted percentage string
 */
export function formatPercentage(value: number, decimals: number = 2): string {
    return `${value.toFixed(decimals)}%`;
}

/**
 * Calculate success rate as percentage
 * @param successful - Number of successful items
 * @param total - Total number of items
 * @returns Success rate as percentage (0-100)
 */
export function calculateSuccessRate(successful: number, total: number): number {
    return total > 0 ? (successful / total) * 100 : 0;
}

type ChangeType = 'increase' | 'decrease' | 'neutral';

/**
 * Get icon for change indicator
 * @param type - The type of change
 * @returns Icon name or null
 */
export function getChangeIcon(type?: ChangeType | string): string | null {
    switch (type) {
    case 'increase':
        return 'mdi-arrow-up';
    case 'decrease':
        return 'mdi-arrow-down';
    case 'neutral':
        return 'mdi-minus';
    default:
        return null;
    }
}

/**
 * Get color for change indicator
 * @param type - The type of change
 * @returns Color name
 */
export function getChangeColor(type?: ChangeType | string): string {
    switch (type) {
    case 'increase':
        return 'success';
    case 'decrease':
        return 'error';
    default:
        return 'default';
    }
}
